import { TONE } from '../../../lib/rcu/catalog/feedback';
import type { Tone } from '../../../lib/rcu/catalog/feedback';

/**
 * How a key state stands against the device.
 *
 * Two different questions are answered here, and the labels keep them apart: what
 * this tablet last wrote, and what the device last said. Neither is proof — a
 * write is only ever *delivered* (the firmware answers `mappingack` with no way
 * to match it to a request), and a read is only ever *what the device held when
 * it answered*.
 */
export const STATE_STATUS = {
  /** Never written from this tablet, and the device has not been read. */
  UNWRITTEN: 'unwritten',
  /** Written once, then edited: the device still holds the older version. */
  STALE: 'stale',
  /** Written and not read back yet. */
  WRITTEN: 'written',
  /** Read back, and the device holds exactly this. */
  MATCHES: 'matches',
  /** Read back, and the device holds something else. */
  DIFFERS: 'differs',
  /** Read back, and the device has no mapping for this key state. */
  NOT_STORED: 'notStored',
  /** The device holds an entry that cannot be read as a mapping. */
  UNREADABLE: 'unreadable',
} as const;

export type StateStatus = (typeof STATE_STATUS)[keyof typeof STATE_STATUS];

export interface StateStatusSpec {
  readonly label: string;
  readonly detail: string;
  readonly tone: Tone;
}

export const STATE_STATUS_SPECS = {
  [STATE_STATUS.UNWRITTEN]: {
    label: 'Not written',
    detail: 'This tablet has not sent this key state to the device.',
    tone: TONE.NEUTRAL,
  },
  [STATE_STATUS.STALE]: {
    label: 'Edited since writing',
    detail: 'The device still holds the version written before this change.',
    tone: TONE.WARNING,
  },
  [STATE_STATUS.WRITTEN]: {
    label: 'Written',
    detail: 'Delivered to the device. Read it back to see what the device holds.',
    tone: TONE.INFO,
  },
  [STATE_STATUS.MATCHES]: {
    label: 'Device matches',
    detail: 'The device answered with exactly this mapping.',
    tone: TONE.SUCCESS,
  },
  [STATE_STATUS.DIFFERS]: {
    label: 'Device differs',
    detail: 'The device answered with a different mapping for this key state.',
    tone: TONE.ERROR,
  },
  [STATE_STATUS.NOT_STORED]: {
    label: 'Not on the device',
    detail: 'The device holds no mapping for this key state.',
    tone: TONE.WARNING,
  },
  [STATE_STATUS.UNREADABLE]: {
    label: 'Unreadable reply',
    detail: 'The device answered with an entry this console cannot read as a mapping.',
    tone: TONE.WARNING,
  },
} as const satisfies Record<StateStatus, StateStatusSpec>;

export const STATE_STATUSES = [
  STATE_STATUS.UNWRITTEN,
  STATE_STATUS.STALE,
  STATE_STATUS.WRITTEN,
  STATE_STATUS.MATCHES,
  STATE_STATUS.DIFFERS,
  STATE_STATUS.NOT_STORED,
  STATE_STATUS.UNREADABLE,
] as const satisfies readonly StateStatus[];

/** True when the device is known to hold something else, so a write is due. */
export const needsWrite = (status: StateStatus): boolean =>
  status === STATE_STATUS.UNWRITTEN ||
  status === STATE_STATUS.STALE ||
  status === STATE_STATUS.DIFFERS ||
  status === STATE_STATUS.NOT_STORED ||
  status === STATE_STATUS.UNREADABLE;

/**
 * What a read run knows about one trigger.
 *
 * The answers are kept apart on purpose. "The device holds nothing there" is not
 * the same as "this tablet has not asked", and neither is the same as "the device
 * answered with something this console cannot read". Collapsing them would let
 * the console claim a mapping is absent when it simply has not looked.
 */
export const DEVICE_ANSWER = {
  /** Not asked since the link came up. */
  UNKNOWN: 'unknown',
  /** Asked, and the device holds no mapping for this trigger. */
  ABSENT: 'absent',
  /** Asked, and it holds this mapping. */
  MAPPING: 'mapping',
  /** Asked, and it holds something this console cannot read as a mapping. */
  UNREADABLE: 'unreadable',
} as const;

export type DeviceAnswerKind = (typeof DEVICE_ANSWER)[keyof typeof DEVICE_ANSWER];
